/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState, useMemo, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { debounceTime } from 'rxjs/operators';

import type ApolloClient from 'apollo-client';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { EnvironmentContext, ForgeUIRenderer } from '@atlassian/forge-ui/ui';
import type { Extension, ViewportDimensions } from '@atlassian/forge-ui/ui';
import { emit, on } from '@atlassian/forge-ui/events';
import type {
	ProductEnvironment,
	CoreData,
	ExtensionData,
	ExtensionViewData,
	ForgeDoc,
	ExtensionPayload,
} from '@atlassian/forge-ui-types';
import { isCustomUI } from '@atlassian/forge-ui';
import { makeADFRenderer } from '@atlassian/forge-ui-kit-adf-renderer';
import type { ExtensionHandlers } from '@atlaskit/editor-common/extensions';

import Blanket from '@atlaskit/blanket';
import Portal from '@atlaskit/portal';
import { layers } from '@atlaskit/theme/constants';
import Spinner from '@atlaskit/spinner';

import { type FlagFunctions, getFlagProvider } from '../../utils/getFlagProvider';
import { type CustomEditContext, type EditorAPI } from '../../types';

import { getCustomBridgeMethods } from './getCustomBridgeMethods';
import { calculateViewportSizeHeight, type ViewportSizeType } from './calculateViewportSizeHeight';
import { fg } from '@atlaskit/platform-feature-flags';
import { useReemitReadyEvent } from '../hooks/useReemitReadyEvent';
import { ChromelessEditor, CommentEditor } from '@atlassian/forge-ui-kit-editor';
import { useMacroStaticContentView } from '../hooks/useMacroStaticContentView';
import type { DocNode } from '@atlaskit/adf-schema';
import { extractExtensionStaticViewProperties } from '../../utils/extractExtensionStaticViewProperties';
import { ReactRenderer } from '@atlaskit/renderer';

const spinnerContainerStyles = css({
	display: 'flex',
	width: '100%',
	height: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	pointerEvents: 'none',
});
export interface ForgeUIExtensionProps {
	accountId: string;
	apolloClient?: ApolloClient<object>;
	contextIds: string[];
	coreData: CoreData;
	customEditContext?: CustomEditContext;
	editorAPI?: EditorAPI;
	environment: ProductEnvironment;
	extension: Extension;
	extensionData: ExtensionData;
	extensionPayload: ExtensionPayload;
	extensionViewData: ExtensionViewData;
	flags: FlagFunctions;
	getContextToken?: () => Promise<string>;
	isEditing: boolean;
	locale: string;
	onForgeDocUpdated?: (forgeDoc?: ForgeDoc) => void;
	productExtensionHandlers?: ExtensionHandlers;
	useRouteActions?: () => {
		[key: string]: (...args: any[]) => any;
		push: (url: string, forceReload?: boolean) => void;
	};
	viewportDimensions?: ViewportDimensions;
}

export const ForgeUIExtension = ({
	accountId,
	apolloClient,
	coreData,
	extensionData,
	extensionViewData,
	extensionPayload,
	environment,
	extension,
	locale,
	flags,
	getContextToken,
	productExtensionHandlers,
	customEditContext,
	isEditing,
	editorAPI,
	onForgeDocUpdated,
	viewportDimensions,
	useRouteActions,
}: ForgeUIExtensionProps): JSX.Element => {
	const { cloudId, localId } = coreData;
	const modalExtension = customEditContext?.modalExtension;
	const bridge = customEditContext?.bridge;
	const memoizedCustomBridgeMethods = useMemo(() => {
		return getCustomBridgeMethods({ isEditing, localId, editorAPI });
	}, [isEditing, localId, editorAPI]);
	const confluenceSpa = useRouteActions?.();

	const [macroConfigForgeDocSubject$] = useState(() => new ReplaySubject(10));
	const configValueRef = useRef(undefined);
	// Stores the forgeDoc for the macro config schema. The ref is often a render behind
	// the actual schema, so should only be used on the initial load.
	const macroConfigForgeDocRef = useRef<ForgeDoc | undefined>(undefined);

	// This sets up an event listener to respond to Editor's requests for the macro config schema.
	// When the GET_CONFIG_FORGE_DOC_${id} event is emitted, the payload includes the current
	// config value, and the resolve and reject functions of a Promise. The listener should be
	// resolving the promise with the value of the macro config forgeDoc.
	const id = `${extension.id}-${coreData.localId}`;
	const reconcile = useCallback(
		({ forgeDoc }: { forgeDoc: ForgeDoc }) => {
			Object.freeze(forgeDoc);
			macroConfigForgeDocRef.current = forgeDoc;
			macroConfigForgeDocSubject$.next(forgeDoc);
			emit(`CONFIG_FORGE_DOC_UPDATED_${id}`);
		},
		[id, macroConfigForgeDocSubject$],
	);

	useEffect(() => {
		const macroConfigForgeDocSubscription = on(
			`GET_CONFIG_FORGE_DOC_${id}`,
			async ({
				resolve,
				config,
			}: {
				config: undefined;
				reject: (reason?: unknown) => void;
				resolve: (value: unknown) => void;
			}) => {
				const subscription = macroConfigForgeDocSubject$.pipe(debounceTime(200)).subscribe({
					next: (forgeDoc) => {
						resolve({
							type: 'Root',
							props: {},
							children: [forgeDoc],
						});
						subscription.unsubscribe();
					},
				});
				configValueRef.current = config;
			},
		);

		return () => {
			macroConfigForgeDocSubscription.unsubscribe();
		};
	}, [id, macroConfigForgeDocSubject$]);

	/*the emitReadyEvent emited by the macro
	must be receive by cc-puppeteer an internal service responsible for
	generating PDFs from confluence pages, the issue is that it does not
	recieve events from other origins such as iframes, so we capture and
	reemit the event here */
	useReemitReadyEvent(fg('forge-ui-reemit-ready-event'));

	const { showFlag, closeFlag } = useMemo(() => getFlagProvider(flags), [flags]);

	const bridgeConfig = useMemo(
		() => ({
			...bridge,
			showFlag,
			closeFlag,
			reconcile,
			spaPush: confluenceSpa?.push,
		}),
		[bridge, showFlag, closeFlag, reconcile, confluenceSpa?.push],
	);

	const AdfRendererWithEmbeddedMacros = useMemo(
		() =>
			makeADFRenderer({
				client: apolloClient,
				bridge: bridgeConfig,
				customBridgeMethods: memoizedCustomBridgeMethods,
				getContextToken,
				extensionData,
				isEditing,
				productExtensionHandlers,
			}),
		[
			apolloClient,
			bridgeConfig,
			getContextToken,
			extensionData,
			memoizedCustomBridgeMethods,
			isEditing,
			productExtensionHandlers,
		],
	);

	let loadingComponent: ReactNode | undefined;

	if (!isCustomUI(extension) && modalExtension) {
		loadingComponent = (
			<Portal zIndex={layers.blanket()}>
				<Blanket isTinted={true} onBlanketClicked={() => modalExtension.closeModalExtension()}>
					<div css={spinnerContainerStyles}>
						<Spinner size="large" />
					</div>
				</Blanket>
			</Portal>
		);
	}

	// Sets the forgeDoc for classic sidebar macro config
	const handleMacroConfigForgeDoc = useCallback(
		(forgeDoc: ForgeDoc) => {
			if (forgeDoc.type === 'MacroConfig') {
				if (customEditContext) {
					throw new Error(
						'ForgeReconciler.addConfig() cannot be called from within a custom config resource',
					);
				}
				macroConfigForgeDocRef.current = forgeDoc;
				macroConfigForgeDocSubject$.next(forgeDoc);
				emit(`CONFIG_FORGE_DOC_UPDATED_${id}`);
			}
		},
		[customEditContext, id, macroConfigForgeDocRef, macroConfigForgeDocSubject$],
	);

	const { isContentViewAvailable, contentKey } = extractExtensionStaticViewProperties(
		extension,
		extensionPayload.config,
	);

	const { isLoading, content } = useMacroStaticContentView<DocNode>({
		extension,
		contentContainerId: extensionData?.content?.id,
		contentKey,
	});

	const shouldRenderStaticContentView =
		!modalExtension && isContentViewAvailable && (isLoading || content);
	if (shouldRenderStaticContentView) {
		return (
			<RendererActionsContext>
				{content ? <ReactRenderer document={content} /> : <Spinner size="small" />}
			</RendererActionsContext>
		);
	}

	return (
		<EnvironmentContext.Provider value={environment}>
			<ForgeUIRenderer
				accountId={accountId}
				bridge={bridgeConfig}
				client={apolloClient}
				cloudId={cloudId!}
				contextIds={[`ari:cloud:confluence::site/${cloudId}`]}
				components={{
					// Override to use latest renderer with embedded macro support for Confluence
					...(fg('platform-forge-ui-kit-adf-renderer')
						? {
								AdfRenderer: AdfRendererWithEmbeddedMacros,
							}
						: {}),
					ChromelessEditor,
					CommentEditor,
				}}
				extension={extension}
				extensionData={extensionData}
				extensionViewData={extensionViewData}
				extensionPayload={extensionPayload}
				customBridgeMethods={memoizedCustomBridgeMethods}
				height={
					extension.properties.viewportSize
						? calculateViewportSizeHeight(extension.properties.viewportSize as ViewportSizeType)
						: undefined
				}
				locale={locale}
				localId={localId}
				getContextToken={getContextToken}
				product="confluence"
				onForgeDocUpdated={(forgeDoc: ForgeDoc) => {
					onForgeDocUpdated?.(forgeDoc);
					handleMacroConfigForgeDoc(forgeDoc);
				}}
				modalExtension={modalExtension}
				onModalExtensionClose={modalExtension?.closeModalExtension}
				loadingComponent={loadingComponent}
				viewportDimensions={viewportDimensions}
			/>
		</EnvironmentContext.Provider>
	);
};
